import { Link } from '@tanstack/react-router';
import './overview.css';
import { UraniaMasthead } from './UraniaMasthead.tsx';

/**
 * Pagina panoramica UranIA — copertina pubblica su `/` (era `/overview`,
 * assorbita da `/` nella fusione A.4.1, "Riordino della mappa delle
 * rotte" §4: due vetrine pubbliche competevano per la stessa visita).
 * `PublicLanding` (la costellazione dei 4 pilastri, la vetrina
 * perdente) è stata rimossa. `/scegli-accesso` (scelta PA/beneficiari)
 * resta una pagina distinta.
 *
 * Dichiara l'identità del prodotto (cielo notturno + sfera armillare
 * come punto della "i") e smista verso le tre "anime": chi programma
 * (Pubblica Amministrazione), chi fa ricerca, chi innova. Nata da un
 * handoff di design esterno ("Copertina — le tre anime").
 *
 * Delle tre aree solo "01 PER CHI PROGRAMMA" ha oggi una destinazione
 * reale (`/login/pa` — il suo hero "Ogni atto, tracciabile." è proprio
 * quello mostrato nello screenshot di riferimento dell'handoff). "02 PER
 * CHI FA RICERCA" e "03 PER CHI INNOVA" non hanno un percorso reale
 * distinto (solo `/login/pa` e `/login/beneficiari` esistono, e
 * quest'ultimo copre già "Impresa, Cittadino o Innovatore" insieme): su
 * richiesta esplicita, restano visibili ma non cliccabili ("In arrivo"),
 * invece di essere forzate su `/login/beneficiari` o su una pagina
 * inesistente.
 *
 * Il CTA secondario "LA PIATTAFORMA" e i tre link del footer (Trasparenza
 * IA, Accessibilità, Area riservata) erano testo statico/disabilitato per
 * lo stesso motivo — nessun equivalente reale. Con le pagine istituzionali
 * del Rilascio A (passo A.7: `/piattaforma`, `/trasparenza-ia`,
 * `/accessibilita`) e `/accedi` (fusione A.4.2) ora esistono: riattivati.
 *
 * Fascia notturna: sfondo blu pieno (nessuna foto) — la foto
 * `urania_hero_bg.jpg` usata inizialmente come placeholder per
 * `cielo-stromboli.png` (licenza non verificata, segnalata dall'handoff
 * stesso) rendeva poco bene ed è stata rimossa su richiesta esplicita.
 *
 * Font: Cormorant Garamond + IBM Plex Sans caricati ad hoc in `index.html`
 * per questa pagina (letterale rispetto all'handoff), in aggiunta a Lora +
 * IBM Plex Mono già in uso nel resto della SPA.
 */
export function OverviewPage() {
  return (
    <div className="uo" data-testid="overview-page">
      <div className="uo-notte">
        <div className="uo-notte__profondita" aria-hidden="true" />
        <div className="uo-notte__velo" aria-hidden="true" />

        <div className="uo-notte__contenuto">
          <div className="uo-servizio">
            <span>UNIONE EUROPEA · POLITICA DI COESIONE 2021–2027</span>
          </div>

          <UraniaMasthead />

          <p className="uo-triade">SCIENZA · AMMINISTRAZIONE · COMUNITÀ</p>
        </div>
      </div>

      <div className="uo-pagina">
        <div className="uo-intro">
          <p className="uo-eyebrow">RICERCA E INNOVAZIONE · UNIONE EUROPEA</p>
          <h2 className="uo-titolo">
            L'intelligenza artificiale che affianca chi programma, chi fa ricerca e chi innova
            nella politica di coesione europea.
          </h2>
          <div className="uo-cta-riga">
            <Link to="/login/pa" className="uo-cta-primario">
              ENTRA →
            </Link>
            <Link to="/piattaforma" className="uo-cta-secondario">
              LA PIATTAFORMA
            </Link>
          </div>
        </div>

        <nav className="uo-griglia" aria-label="Le tre anime di UranIA">
          <Link to="/login/pa" className="uo-colonna" data-testid="overview-area-pa">
            <span className="uo-colonna__numero">01</span>
            <span className="uo-colonna__titolo">PER CHI PROGRAMMA</span>
            <span className="uo-colonna__descrizione">
              Per chi guida la programmazione, l'attuazione e la rendicontazione dei fondi della
              politica di coesione, con ogni atto tracciato e ogni fonte citata.
            </span>
          </Link>

          <div
            className="uo-colonna uo-colonna--presto"
            aria-disabled="true"
            data-testid="overview-area-ricerca"
          >
            <span className="uo-colonna__numero">02</span>
            <span className="uo-colonna__titolo">
              PER CHI FA RICERCA <span className="uo-tag-presto">In arrivo</span>
            </span>
            <span className="uo-colonna__descrizione">
              Per chi propone progetti di ricerca e innovazione e ha bisogno di orientarsi tra
              bandi, avvisi e requisiti di ammissibilità.
            </span>
          </div>

          <div
            className="uo-colonna uo-colonna--presto"
            aria-disabled="true"
            data-testid="overview-area-community"
          >
            <span className="uo-colonna__numero">03</span>
            <span className="uo-colonna__titolo">
              PER CHI INNOVA <span className="uo-tag-presto">In arrivo</span>
            </span>
            <span className="uo-colonna__descrizione">
              Per imprese, enti e cittadini che vogliono contribuire alla comunità dei fondi della
              politica di coesione.
            </span>
          </div>
        </nav>

        <footer className="uo-footer">
          <span>© UNIONE EUROPEA</span>
          <span>
            <Link to="/trasparenza-ia" style={{ color: 'inherit', textDecoration: 'none' }}>
              TRASPARENZA IA
            </Link>
            {' · '}
            <Link to="/accessibilita" style={{ color: 'inherit', textDecoration: 'none' }}>
              ACCESSIBILITÀ
            </Link>
            {' · '}
            <Link to="/accedi" style={{ color: 'inherit', textDecoration: 'none' }}>
              AREA RISERVATA
            </Link>
          </span>
        </footer>
      </div>
    </div>
  );
}
